import type { LucideIcon } from "lucide-react";
import {
  Calendar,
  CarFront,
  CreditCard,
  Globe,
  Wallet,
  KeyRound,
  Mail,
  MapPin,
  MonitorSmartphone,
  PlugZap,
  ScanLine,
  ShieldCheck,
  Upload,
  FileText,
} from "lucide-react";

export type SchnittstellenIntegrationId =
  | "email"
  | "pms"
  | "tourist"
  | "tourist-import"
  | "google-places"
  | "channel-api"
  | "tse"
  | "zvt"
  | "stripe"
  | "device-service"
  | "fernauslese"
  | "barrier"
  | "lpr"
  | "events";

export interface SchnittstellenIntegrationDef {
  id: SchnittstellenIntegrationId;
  label: string;
  shortLabel: string;
  description: string;
  icon: LucideIcon;
  /** Tailwind classes for icon background accent */
  accentClass: string;
  /** Tailwind classes for icon color */
  iconClass: string;
}

export const SCHNITTSTELLEN_INTEGRATIONS: SchnittstellenIntegrationDef[] = [
  {
    id: "email",
    label: "E-Mail-Versand",
    shortLabel: "E-Mail",
    description:
      "SMTP oder Microsoft Graph für Rechnungen, Buchungsbestätigungen und automatische Benachrichtigungen.",
    icon: Mail,
    accentClass: "bg-blue-500/10",
    iconClass: "text-blue-500",
  },
  {
    id: "pms",
    label: "PMS-Anbindung",
    shortLabel: "PMS",
    description:
      "Automatischer Versand der PMS-Import-XML bei jeder Online-Buchung an Ihr Property-Management-System.",
    icon: Upload,
    accentClass: "bg-violet-500/10",
    iconClass: "text-violet-500",
  },
  {
    id: "tourist",
    label: "Tourist-Anbindung",
    shortLabel: "Tourist",
    description:
      "Online-Buchung mit Tourist-PMS per E-Mail-Anbindung, Ausgebucht-Tage und deaktivierter Verfügbarkeitsprüfung.",
    icon: Globe,
    accentClass: "bg-cyan-500/10",
    iconClass: "text-cyan-500",
  },
  {
    id: "tourist-import",
    label: "Tourist-Import",
    shortLabel: "Import",
    description:
      "Einmaliger Datenimport aus Tourist Pro (SQL/CSV): Gäste, Buchungen, Rechnungen und Zuordnungen.",
    icon: FileText,
    accentClass: "bg-orange-500/10",
    iconClass: "text-orange-500",
  },
  {
    id: "google-places",
    label: "Google Places",
    shortLabel: "Places",
    description:
      "Adress-Autocomplete für Straße, PLZ und Ort in der Online-Buchungsstrecke – optional, aber komfortabler für Gäste.",
    icon: MapPin,
    accentClass: "bg-emerald-500/10",
    iconClass: "text-emerald-500",
  },
  {
    id: "channel-api",
    label: "Channel-Partner API",
    shortLabel: "Channel-API",
    description:
      "API-Zugang für Channel-Partner wie camping.info oder RESAVIO, um Buchungen direkt in CampDesk zu übermitteln.",
    icon: KeyRound,
    accentClass: "bg-amber-500/10",
    iconClass: "text-amber-500",
  },
  {
    id: "tse",
    label: "TSE / Kassensicherung",
    shortLabel: "TSE",
    description:
      "Gesetzliche Kassensicherung (Deutschland): Online-TSE-Anbieter für die Signierung von Bar- und Kartenzahlungen.",
    icon: ShieldCheck,
    accentClass: "bg-green-500/10",
    iconClass: "text-green-500",
  },
  {
    id: "zvt",
    label: "EC-Kartenterminal (ZVT)",
    shortLabel: "ZVT",
    description:
      "Kartenzahlungen über das EC-Terminal an Rezeption, Schnellverkauf und Gäste-Tablet via CampingDeviceService.",
    icon: CreditCard,
    accentClass: "bg-indigo-500/10",
    iconClass: "text-indigo-500",
  },
  {
    id: "stripe",
    label: "Stripe",
    shortLabel: "Stripe",
    description:
      "Online-Zahlungen für Gäste-Shop und Comfort Check-in über Ihren Stripe-Zugang.",
    icon: Wallet,
    accentClass: "bg-fuchsia-500/10",
    iconClass: "text-fuchsia-500",
  },
  {
    id: "device-service",
    label: "CampingDeviceService",
    shortLabel: "Geräte",
    description:
      "Lokaler Dienst für Bondrucker, EC-Terminal und weitere Peripheriegeräte an der Kassen-Workstation.",
    icon: MonitorSmartphone,
    accentClass: "bg-slate-500/10",
    iconClass: "text-slate-400",
  },
  {
    id: "fernauslese",
    label: "Fernauslese.de",
    shortLabel: "Strom",
    description:
      "Steuerung der Stromsteckdosen über die Fernauslese.de API – Check-in einschalten, Check-out ausschalten.",
    icon: PlugZap,
    accentClass: "bg-yellow-500/10",
    iconClass: "text-yellow-500",
  },
  {
    id: "barrier",
    label: "Schranken / Zufahrt",
    shortLabel: "Schranke",
    description:
      "Magnetic, Hinn, SKIDATA und generische Webhooks – Freigabe bei Check-in, Sperre bei Check-out.",
    icon: CarFront,
    accentClass: "bg-teal-500/10",
    iconClass: "text-teal-500",
  },
  {
    id: "lpr",
    label: "Kennzeichenerkennung",
    shortLabel: "LPR",
    description:
      "Arnold, Hikvision, Axis, Tattile und generische Kameras – Kennzeichen-Events empfangen und Schranke öffnen.",
    icon: ScanLine,
    accentClass: "bg-sky-500/10",
    iconClass: "text-sky-500",
  },
  {
    id: "events",
    label: "Veranstaltungs-APIs",
    shortLabel: "Events",
    description:
      "Eventbrite, Ticketmaster und Eventim für den Infobildschirm – externe Veranstaltungen automatisch synchronisieren.",
    icon: Calendar,
    accentClass: "bg-rose-500/10",
    iconClass: "text-rose-500",
  },
];

/** Legacy section → first matching integration (for redirects). */
const LEGACY_SECTION_MAP: Record<string, SchnittstellenIntegrationId> = {
  email: "email",
  booking: "pms",
  kasse: "tse",
  "device-service": "device-service",
  strom: "fernauslese",
  schranke: "barrier",
  barrier: "barrier",
  lpr: "lpr",
  kennzeichen: "lpr",
  events: "events",
  "tourist-import": "tourist-import",
};

export function getIntegrationDef(
  id: SchnittstellenIntegrationId,
): SchnittstellenIntegrationDef {
  return SCHNITTSTELLEN_INTEGRATIONS.find((i) => i.id === id)!;
}

export function parseSchnittstellenIntegration(
  value: string | null,
): SchnittstellenIntegrationId | null {
  if (!value) return null;
  return SCHNITTSTELLEN_INTEGRATIONS.some((i) => i.id === value)
    ? (value as SchnittstellenIntegrationId)
    : null;
}

export function legacySectionToIntegration(
  section: string | null,
): SchnittstellenIntegrationId | null {
  if (!section) return null;
  return LEGACY_SECTION_MAP[section] ?? null;
}

export function schnittstellenIntegrationHref(id: SchnittstellenIntegrationId): string {
  return `/stammdaten/schnittstellen?integration=${id}`;
}

export function schnittstellenOverviewHref(): string {
  return "/stammdaten/schnittstellen";
}
